import { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { ThumbUp, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { AppButton } from '../../../shared/components/buttons/AppButton';
import offerDetailService from '../services/offerDetailService';
import { OfferDetail } from '../types/offer-detail.types';

interface ImInterestedButtonProps {
  offer: OfferDetail;
  onDealClosed?: () => void; // Callback to refresh offer after successful deal
  isOwnerView?: boolean; // When true, renders "Close my offer" for the offer owner
}

export default function ImInterestedButton({
  offer,
  onDealClosed,
  isOwnerView = false,
}: Readonly<ImInterestedButtonProps>) {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState<'VENTE_CONCLUE' | 'AUTRE' | null>(null);
  const isSubmittingRef = useRef(false);
  const { showSnackbar } = useSnackbar();
  const { user } = useCurrentUser();

  // Check if offer is expired — compare against start of today (midnight)
  // so offers ending today remain usable all day (consistent with backend filter)
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const isExpired = new Date(offer.endDate) < startOfToday;

  // Check if offer is already closed
  const isClosed = offer.status === 'CLOSED';

  // Check if offer is not published/available
  const isUnavailable = offer.status !== 'PUBLISHED' && !isClosed;

  // Check if user is trying to purchase their own offer
  const isSelfPurchase = user?.id === offer.userId;

  // Check if current buyer already expressed interest in this offer
  const isAlreadyInterested = !isOwnerView && Boolean(offer.alreadyInterested);

  // Button should be disabled if offer is expired (buyers only), closed, unavailable, or self-purchase
  // When isOwnerView, expired and self-purchase restrictions are removed (owner can close their own offer)
  const isDisabled =
    (!isOwnerView && isExpired) ||
    isClosed ||
    isUnavailable ||
    isAlreadyInterested ||
    (!isOwnerView && isSelfPurchase) ||
    isLoading;

  const handleClick = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    if (!isLoading) {
      setOpenDialog(false);
      setSelectedReason(null);
    }
  };

  const handleConfirm = async () => {
    if (isSubmittingRef.current || isLoading) {
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      if (isOwnerView) {
        await offerDetailService.closeOffer(offer.id, selectedReason!);
        showSnackbar(t('offerDetail.messages.offerCloseSuccess'), { variant: 'success' });
      } else {
        await offerDetailService.interestOffer(offer.id);
        showSnackbar(t('offerDetail.messages.dealSuccess'), { variant: 'success' });
      }
      setOpenDialog(false);

      // Call callback to refresh offer data (will show updated status)
      if (onDealClosed) {
        onDealClosed();
      }
    } catch (error) {
      console.error('Failed to close deal:', error);

      // Backend returns i18n error codes (e.g., 'errors.offer.expired')
      const errorCode =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '';

      // Map backend error codes to frontend translation keys
      const errorKeyMap: Record<string, string> = {
        'errors.offer.expired': 'offerDetail.messages.offerExpired',
        'errors.offer.unavailable': 'offerDetail.messages.offerUnavailable',
        'errors.offer.cannotPurchaseOwn': 'offerDetail.messages.cannotPurchaseOwnOffer',
      };

      const fallbackError = isOwnerView
        ? 'offerDetail.messages.offerCloseError'
        : 'offerDetail.messages.dealError';
      const errorMessage = errorKeyMap[errorCode] ? t(errorKeyMap[errorCode]) : t(fallbackError);

      showSnackbar(errorMessage, { variant: 'error' });
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  let startIcon = <ThumbUp />;
  if (isOwnerView) {
    startIcon = <Cancel />;
  }
  if (isLoading) {
    startIcon = <CircularProgress size={20} color="inherit" />;
  }

  return (
    <>
      <AppButton
        variant="primary"
        size="small"
        fullWidth
        startIcon={startIcon}
        onClick={handleClick}
        disabled={isDisabled}
        sx={
          isOwnerView
            ? {
                bgcolor: 'error.light',
                color: 'error.main',
              boxShadow: 'none',
                '&:hover': {
                  bgcolor: 'error.light',
                  filter: 'brightness(0.95)',
                  boxShadow: 'none',
                },
                '&.Mui-disabled': { bgcolor: 'error.light', color: 'error.main', opacity: 0.6 },
              }
            : { boxShadow: 2 }
        }
        data-testid="offer-detail-im-interested-button"
      >
        {isOwnerView ? t('offerDetail.closeOffer') : t('offerDetail.concludeDeal')}
      </AppButton>

      <Dialog
        open={openDialog}
        onClose={handleClose}
        data-testid="offer-detail-im-interested-dialog"
      >
        <DialogTitle>
          {isOwnerView
            ? t('offerDetail.closeOfferDialog.title')
            : t('offerDetail.concludeDealDialog.title')}
        </DialogTitle>
        <DialogContent>
          {isOwnerView ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {t('offerDetail.closeOfferDialog.question')}
              </DialogContentText>
              <FormControl component="fieldset">
                <RadioGroup
                  value={selectedReason ?? ''}
                  onChange={(e) => setSelectedReason(e.target.value as 'VENTE_CONCLUE' | 'AUTRE')}
                >
                  <FormControlLabel
                    value="VENTE_CONCLUE"
                    control={<Radio />}
                    label={t('offerDetail.closeOfferDialog.optionSale')}
                    data-testid="offer-detail-close-reason-sale"
                  />
                  <FormControlLabel
                    value="AUTRE"
                    control={<Radio />}
                    label={t('offerDetail.closeOfferDialog.optionOther')}
                    data-testid="offer-detail-close-reason-other"
                  />
                </RadioGroup>
              </FormControl>
            </>
          ) : (
              <DialogContentText>
                {t('offerDetail.concludeDealDialog.message')}
              </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <AppButton
            onClick={handleClose}
            variant="text"
            disabled={isLoading}
            data-testid="offer-detail-im-interested-dialog-cancel"
          >
            {t('offerDetail.concludeDealDialog.cancel')}
          </AppButton>
          <AppButton
            onClick={handleConfirm}
            variant="primary"
            disabled={isLoading || (isOwnerView && selectedReason === null)}
            data-testid="offer-detail-im-interested-dialog-confirm"
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : isOwnerView ? (
              t('offerDetail.closeOfferDialog.confirm')
            ) : (
              t('offerDetail.concludeDealDialog.confirm')
            )}
          </AppButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
